import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * The rendered width of one element, kept current as it changes.
 *
 * A `ResizeObserver` and not a window listener: a table's width is the
 * CONTAINER's, which moves when the sidebar collapses or a neighbouring panel
 * changes, none of which resizes the window.
 *
 * `0` until the element is measured (and always under jsdom), so callers treat
 * `0` as "unknown" rather than "very narrow".
 */
export function useElementWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measure once up front: the observer fires on its own soon after, but not
    // before the first paint, and a frame at `0` is a frame laid out for the
    // wrong width.
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}
